import { type ReactNode, useEffect, useMemo, useState } from 'react'
import axios from 'axios'

import {
    clearAuthStorage,
    getAccessToken,
    refreshAccessToken,
    seedDevSession,
} from '@/shared/api/auth'
import { getLocalStorageValue, setLocalStorageValue } from '@/shared/hooks/use-local-storage'
import { captureSentryException } from '@/shared/lib/capture-sentry-error'
import { getJwtState } from '@/shared/lib/getJwtState'

type TAuthGuardStatus = 'checking' | 'failed' | 'ready'

const getAuthStep = (error: unknown): string => {
    if (!axios.isAxiosError(error)) {
        return 'jwt-check'
    }

    if (error.config?.url?.includes('/refresh')) {
        return 'refresh-token'
    }

    return 'get-access-token'
}

export const UserSettings = ({
    children,
    fallback,
}: {
    children: ReactNode
    fallback: (title: string, description: string, error: string) => ReactNode
}) => {
    const [status, setStatus] = useState<TAuthGuardStatus>('checking')
    const [fallbackData, setFallbackData] = useState<null | {
        description: string
        error: string
        title: string
    }>(null)

    // Важно: фиксируем params один раз на маунте, чтобы не пересчитывать на каждом рендере
    const searchParams = useMemo(() => new URLSearchParams(globalThis.location.search), [])
    const phoneFromUrl = searchParams.get('phone')?.replaceAll('"', '') ?? ''
    const tokenFromUrl = searchParams.get('token')?.replaceAll('"', '') ?? ''

    useEffect(() => {
        let cancelled = false
        const fail = (
            title: string,
            description: string,
            error: string,
            clearDidCredentials = true,
        ) => {
            clearAuthStorage({ clearDidCredentials })
            setFallbackData({ title, description, error })
            setStatus('failed')
        }

        const run = async () => {
            try {
                // DEV-only: засев токена из `make gen-jwt` в обход сломанного локально DID-bootstrap.
                // В проде `seedDevSession` сразу возвращает false (см. import.meta.env.DEV).
                const devAccessFromUrl = searchParams.get('access')?.replaceAll('"', '') ?? ''

                if (seedDevSession(devAccessFromUrl)) {
                    if (!cancelled) {
                        setStatus('ready')
                    }

                    return
                }

                const phoneFromStorage = getLocalStorageValue('user-phone', '')
                const tokenFromStorage = getLocalStorageValue('user-token', '')

                const hasUrlPhone = Boolean(phoneFromUrl)
                const hasUrlToken = Boolean(tokenFromUrl)
                const hasUrlCreds = hasUrlPhone && hasUrlToken

                const hasStoredPhone = Boolean(phoneFromStorage)
                const hasStoredToken = Boolean(tokenFromStorage)
                const hasStoredCreds = hasStoredPhone && hasStoredToken

                // 1) Валидация входных DID-данных
                if ((hasUrlPhone || hasUrlToken) && !hasUrlCreds) {
                    return fail(
                        'Некорректные данные авторизации',
                        'В ссылке переданы не все параметры авторизации. Откройте экран заново из приложения.',
                        'Incomplete phone/token in URL',
                    )
                }

                if (hasUrlCreds) {
                    if (phoneFromStorage !== phoneFromUrl || tokenFromStorage !== tokenFromUrl) {
                        // меняем DID-пару: чистим только session-часть
                        clearAuthStorage({ clearDidCredentials: false })
                    }
                    setLocalStorageValue('user-phone', phoneFromUrl)
                    setLocalStorageValue('user-token', tokenFromUrl)
                } else if ((hasStoredPhone || hasStoredToken) && !hasStoredCreds) {
                    return fail(
                        'Некорректные данные авторизации',
                        'Сохраненные данные авторизации повреждены. Откройте экран заново из приложения.',
                        'Incomplete phone/token in storage',
                    )
                } else if (!hasStoredCreds) {
                    return fail(
                        'Нет данных авторизации',
                        'Откройте экран заново из мобильного приложения',
                        'Missing phone/token',
                    )
                }

                const phone = getLocalStorageValue('user-phone', '')
                const token = getLocalStorageValue('user-token', '')

                try {
                    let accessToken = getLocalStorageValue('access-token', '')
                    let refreshToken = getLocalStorageValue('refresh-token', '')

                    if (!accessToken || !refreshToken) {
                        const response = await getAccessToken(phone, token)

                        accessToken = response.access
                        refreshToken = response.refresh
                    }

                    const jwtState = getJwtState(accessToken)

                    if (jwtState.isExpired) {
                        if (!refreshToken) {
                            throw new Error('Missing refresh token')
                        }
                        await refreshAccessToken(refreshToken).catch(async (error) => {
                            if (axios.isAxiosError(error) && error.response?.status === 401) {
                                await getAccessToken(phone, token)

                                return
                            }
                            throw error
                        })
                    }

                    if (!cancelled) {
                        setStatus('ready')
                    }
                } catch (error) {
                    captureSentryException(error, {
                        tags: {
                            errorType: 'user-settings-auth-check-failed',
                        },
                        extra: {
                            authStep: getAuthStep(error),
                            hasStoredAccessToken: Boolean(getLocalStorageValue('access-token', '')),
                            hasStoredRefreshToken: Boolean(
                                getLocalStorageValue('refresh-token', ''),
                            ),
                            jwtExpired: getJwtState(getLocalStorageValue('access-token', ''))
                                .isExpired,
                        },
                    })

                    return fail(
                        'Не удалось авторизоваться',
                        'Сессия недействительна. Откройте экран заново из приложения.',
                        'Auth check failed',
                    )
                }
            } catch (error) {
                captureSentryException(error, {
                    tags: {
                        errorType: 'user-settings-auth-check-failed',
                    },
                    extra: {
                        authStep: 'outer-catch',
                    },
                })

                return fail(
                    'Не удалось авторизоваться',
                    'Сессия недействительна. Откройте экран заново из приложения.',
                    'Auth check failed',
                )
            }
        }

        void run()

        return () => {
            cancelled = true
        }
    }, [phoneFromUrl, tokenFromUrl, searchParams])

    if (status === 'failed' && fallbackData) {
        return <>{fallback(fallbackData.title, fallbackData.description, fallbackData.error)}</>
    }

    return <>{children}</>
}
